import { Badge, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger, Label, Switch } from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn, toApiSort } from "@/lib/utils";
import { setBulkUploadDtl, setCommunityData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useBulkDownloadUser, useDownloadUserTemplate, useGetCommunities, useGetOwnerMasterList, useUpdateUserActiveMutation } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { MoreVertical, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "@/components/Toast";

// owner_master_grid_view.user_classification (V1.164)
const CLASSIFICATION_LABELS: Record<string, string> = {
  OWNER: "Owner",
  TENANT: "Tenant",
  OWNER_TENANT: "Owner / Tenant",
};

const statusBadge = (active: boolean, on: string, off: string) => (
  <Badge
    variant="outline"
    className={cn(
      active ? "border-green-600 bg-green-100 text-green-600" : "border-red-600 bg-red-100 text-red-600",
      "rounded-full px-4 py-2"
    )}
  >
    {active ? on : off}
  </Badge>
);

const ALL_COMMUNITIES = "all";

const useOwners = () => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  // Opens on "All Communities" (admin: every community, others: their assigned ones; one row per user),
  // or on ?communityId= when a sub-page (Bulk Upload, Upload History) links back to this grid.
  // Kept local so other pages' shared community selection isn't changed to "all".
  const [searchParams, setSearchParams] = useSearchParams();
  const [communityId, setCommunityId] = useState<string>(searchParams.get("communityId") || ALL_COMMUNITIES);
  const [communityKey, setCommunityKey] = useState(Math.random() * 2);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sorting, setSorting] = useState([{ id: "userId", desc: true }]);
  const [openResetDialog, setOpenResetDialog] = useState<any>(false);
  const selectedRow = useRef<any>(null);
  const isAdmin = location.pathname.startsWith("/admin");
  
  // + Add User is admin only (exceptions); owners are normally created through Property Ownership
  const hasAddPermission = isAdmin;
  // Login column + toggle (PATCH /users/{id}/active) are admin only
  const canToggleStatus = isAdmin;
  
  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunities({});
    
  useEffect(() => {
    if (communityList?.data) {
      setCommunityKey(Math.random() * 3);
      // Opened on ?communityId=: make it the current community too (Add User / Bulk Upload act on it)
      if (communityId !== ALL_COMMUNITIES && communityData?.communityId?.toString() !== communityId) {
        const details = communityList.data.find((data: any) => data.communityId == communityId);
        if (details) dispatch(setCommunityData(details));
      }
    }
  }, [communityList]);

  // + Add creates the owner (person); the property is attached later in Property Ownership
  const onAdd = () => {
    if (communityId === ALL_COMMUNITIES || communityData?.communityId?.toString() !== communityId) {
      toast.error("Select a community first — the new user is added to it.");
      return;
    }
    navigate(routes.createOwner);
  }
  
  const {
    data: usersGridData,
    isLoading: isUserGridLoading,
    refetch: refetchUserGridData,
    // isRefetching: isRefetchingUserData
  } = useGetOwnerMasterList({
    data: {
      filters: [
        ...(communityId === ALL_COMMUNITIES ? [] : [{
          field: "communityId",
          value: communityId,
          operator: "EQUAL",
        }])
      ],
      searchFields: ["firstName", "lastName", "userName", "email", "phoneNumber"],
      search: globalFilter,
      pageSize,
      pageNumber,
      sort: toApiSort(sorting, { field: "userId", direction: "DESC" }),
    },
    queryParams: {
      enabled: !!communityId
    }
  });
  
  useEffect(() => {
    refetchUserGridData();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchUserGridData();
    }
  }, [globalFilter]);

  const onCommunityChange = (e: any) => {
    setPageNumber(0);
    setCommunityId(e);
    setSearchParams(e !== ALL_COMMUNITIES ? { communityId: e } : {}, { replace: true });
    if (e !== ALL_COMMUNITIES) {
      // + Add User creates the user in this community
      const details = communityList?.data?.find((data: any) => data.communityId == e);
      dispatch(setCommunityData(details));
    }
    setTimeout(() => refetchUserGridData(), 100)
  };

  const communityOptions = [
    { value: ALL_COMMUNITIES, label: "All Communities" },
    ...(communityList?.data?.map((data: any) => ({ value: data?.communityId?.toString(), label: data?.name })) ?? []),
  ];
  
  const { mutate: updateUserActive, isPending: isUpdateUserActivePending } =
    useUpdateUserActiveMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
      },
      onSuccess: () => {
        toast.success("User login status updated successfully");
        queryClient.invalidateQueries({ queryKey: ["owner-master"] });
      },
    });

  const columns = [
    {
      accessorKey: "userId",
      header: "User ID",
    },
    {
      accessorKey: "userName",
      header: "User Name",
    },
    {
      accessorKey: "firstName",
      header: "First Name",
    },
    {
      accessorKey: "lastName",
      header: "Last Name",
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "phoneNumber",
      header: "Mobile No.",
    },
    {
      accessorKey: "userClassification",
      header: "User Classification",
      cell: ({ row }: any) => CLASSIFICATION_LABELS[row.original.userClassification] ?? "—",
    },
    {
      accessorKey: "ownerType",
      header: "Owner Type",
      cell: ({ row }: any) => row.original.ownerType || "—",
    },
    {
      accessorKey: "propertyCount",
      header: "Properties",
    },
    {
      // Active only while the user holds at least one active Owner / Tenant role in the community
      accessorKey: "roleActive",
      header: "Status",
      cell: ({ row }: any) => statusBadge(row.original.roleActive, "Active", "Inactive"),
    },
    ...(isAdmin ? [{
      // Login needs the account switched on (Actions) AND an active Owner / Tenant role in some community
      accessorKey: "active",
      header: "Login",
      cell: ({ row }: any) => statusBadge(row.original.active && row.original.hasLoginRole, "Enabled", "Disabled"),
    }] : []),
    ...(isAdmin ? [{
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }: any) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-7 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              {canToggleStatus ? <DropdownMenuLabel className="flex items-center gap-2">
                <div
                  className="flex items-center space-x-2"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Switch
                    id={`active-${row.original.userId}`}
                    onCheckedChange={(isActive: any) => {
                      updateUserActive({
                        userId: row.original.userId,
                        active: Boolean(isActive),
                      });
                    }}
                    checked={row.original.active && row.original.hasLoginRole}
                    // no active Owner / Tenant role anywhere: login stays disabled (backend refuses too)
                    disabled={isUpdateUserActivePending || !row.original.hasLoginRole}
                  />
                  <Label htmlFor={`active-${row.original.userId}`}>
                    {!row.original.hasLoginRole ? "Login disabled (no active role)"
                      : row.original.active ? "Login enabled" : "Login disabled"}
                  </Label>
                </div>
              </DropdownMenuLabel> : <></>}
              {isAdmin ? <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setOpenResetDialog(true);
                  selectedRow.current = row.original;
                }}
              >
                <RotateCcw className="w-4 h-4" />
                Reset Password
              </DropdownMenuItem> : <></>}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    }] : []),
  ];

  // Detail page exists on the Admin and CAM menus only (not BOM /owner)
  const onRowClick = location.pathname.startsWith("/owner")
    ? undefined
    : (row: any) => navigate(`${routes.details}/${row.original.userId}`);

  const {
    data: downloadTemplateData,
    refetch: refetchTemplateData,
    isRefetching: isTemplateDownloading,
  } = useDownloadUserTemplate({ 
    communityId: communityId,
    queryParams: {
      enabled: false 
    }
  });

  const {
    data: bulkDownload,
    refetch: refetchBulkData,
    isRefetching: isBulkDownloading,
  } = useBulkDownloadUser({
    communityId: communityId as string,
    queryParams: {
      enabled: false,
    },
  });

  useEffect(() => {
    if (downloadTemplateData) {
      try {
        const response = downloadTemplateData;
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let downloadFileName = `user-template.xlsx`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=\"?([^\"]+)\"?/);
          if (match?.[1]) {
            downloadFileName = match[1];
          }
        }

        link.setAttribute("download", downloadFileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            "There is an issue while downloading the file"
        );
      } finally {
        queryClient.removeQueries({ queryKey: ["download-user-template", communityId] });
      }
    }
  }, [downloadTemplateData, communityId]);

  useEffect(() => {
    if (bulkDownload) {
      try {
        const response = bulkDownload;
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let downloadFileName = `${communityId}-users.xlsx`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=\"?([^\"]+)\"?/);
          if (match?.[1]) {
            downloadFileName = match[1];
          }
        }

        link.setAttribute("download", downloadFileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            "There is an issue while downloading the file"
        );
      } finally {
        queryClient.removeQueries({ queryKey: ["bulk-download-user", communityId] });
      }
    }
  }, [bulkDownload, communityId]);
  
  const downloadTemplate = () => {
    refetchTemplateData();
  };

  const onBulkDownload = () => {
    refetchBulkData();
  };
  
  const onBulkUpload = () => {
    dispatch(setBulkUploadDtl(null));
    navigate(`${routes.bulkUpload}`)
  }

  // Past owner uploads of the selected community and what happened to each row
  const onUploadHistory = () => navigate(`${routes.uploadHistory}`);

  return {
    isMobile,
    onAdd,
    globalFilter,
    setGlobalFilter,
    communityOptions,
    isAllCommunities: communityId === ALL_COMMUNITIES,
    isCommunityLoading,
    communityId,
    onCommunityChange,
    isLoading: isUserGridLoading,
    usersGridData,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    columns,
    onRowClick,
    hasAddPermission,
    communityKey,
    isTemplateDownloading,
    isBulkDownloading,
    downloadTemplate,
    onBulkDownload,
    onBulkUpload,
    onUploadHistory,
    openResetDialog,
    setOpenResetDialog,
    selectedRow: selectedRow.current,
    isAdmin
  }
}

export default useOwners;